import { get, ref } from "firebase/database";
import { database } from "../firebase/firebaseConfig";
import {
  Constants,
  DEFAULT_SUPPORT_EMAIL,
} from "../../domain/models/Constants";
import { ConstantsConverter } from "./converters/ConstantsConverter";
import { CoreError, CoreErrorCause } from "../../domain/models/CoreError";

export class StaticDataService {
  // Singleton instance
  private static instance: StaticDataService;

  private cachedConstants: Constants | null = null;
  private fetchPromise: Promise<Constants> | null = null;

  // Private constructor to enforce singleton usage
  private constructor() {}

  // Global accessor to retrieve the singleton instance
  public static getInstance(): StaticDataService {
    if (!StaticDataService.instance) {
      StaticDataService.instance = new StaticDataService();
    }
    return StaticDataService.instance;
  }

  /**
   * Gets the cached constants. Returns null if constants haven't been fetched yet.
   * Use this for synchronous access to constants that were previously fetched.
   *
   * @returns Constants object or null if not yet loaded
   */
  public getConstants(): Constants | null {
    return this.cachedConstants;
  }

  public getSupportEmail(): string {
    return this.cachedConstants?.supportEmail ?? DEFAULT_SUPPORT_EMAIL;
  }

  /**
   * Fetches constants from Firebase Realtime Database and caches them in the singleton instance.
   * If constants are already cached, this will refresh them from the server.
   * If a fetch is already in progress, returns the existing promise.
   *
   * @param forceRefresh If true, forces a fresh fetch even if constants are cached
   * @returns Promise resolving to Constants object
   * @throws CoreError if fetching or parsing fails
   */
  public async fetchConstants(
    forceRefresh: boolean = false,
  ): Promise<Constants> {
    // If constants are cached and not forcing refresh, return cached
    if (this.cachedConstants && !forceRefresh) {
      return this.cachedConstants;
    }

    // If a fetch is already in progress, return that promise
    if (this.fetchPromise && !forceRefresh) {
      return this.fetchPromise;
    }

    // Start new fetch
    this.fetchPromise = this.performFetch();

    try {
      const constants = await this.fetchPromise;
      this.cachedConstants = constants;
      return constants;
    } catch (error) {
      // Clear the promise on error so we can retry
      this.fetchPromise = null;
      throw error;
    }
  }

  /**
   * Performs the actual fetch operation from Firebase.
   * @private
   */
  private async performFetch(): Promise<Constants> {
    try {
      const snapshot = await get(ref(database, "constants"));

      if (!snapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.ConstantsCouldNotBeParsed,
          context: "Constants snapshot does not exist",
        });
      }

      const data = snapshot.val();
      return ConstantsConverter.domainModel(data);
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw new CoreError({
        cause: CoreErrorCause.ConstantsCouldNotBeParsed,
        context: "Error fetching constants",
        underlyingException: error,
      });
    }
  }
}
